import React, { useContext, useState, useMemo } from 'react';
import { AppContext } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const { FiType, FiStar, FiUsers, FiCalendar, FiImage, FiX, FiPlus, FiAlignLeft, FiAlignCenter, FiAlignRight, FiEye, FiEyeOff, FiTrash2, FiLayout, FiSettings, FiGlobe, FiEdit2, FiCheck, FiMenu, FiCornerDownRight, FiFolder, FiBookOpen, FiAward, FiBriefcase } = FiIcons;

const stripHtml = (html) => html ? html.replace(/<[^>]*>?/gm, '') : '';

const SpacingSlider = ({ label, value, onChange, min = 0, max = 30 }) => (
  <div className="bg-black/30 p-4 border border-white/5 space-y-3">
    <div className="flex justify-between items-center">
      <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em]">{label}</label>
      <span className="font-mono text-xs text-white bg-white/10 px-2 py-0.5 rounded">{value} rem</span>
    </div>
    <input type="range" min={min} max={max} step="0.5" value={value || 0} onChange={e => onChange(Number(e.target.value))} className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-white" />
  </div>
);

const ContentBlock = ({ label, styleKey }) => {
  const { textStyles, updateTextStyle } = useContext(AppContext);
  const s = textStyles[styleKey] || {};
  return (
    <div className="bg-black/20 border border-white/5 p-3 flex flex-col gap-3">
      <div className="flex items-center justify-between border-b border-white/5 pb-2">
        <label className="text-[10px] uppercase font-bold text-white/40">{label}</label>
        <div className="flex items-center gap-4">
          <div className="flex bg-black/40 p-0.5 rounded border border-white/5">
            {[{ id: 'left', icon: FiAlignLeft }, { id: 'center', icon: FiAlignCenter }, { id: 'right', icon: FiAlignRight }].map(btn => (
              <button key={btn.id} onClick={() => updateTextStyle(styleKey, 'align', btn.id)} className={`p-1.5 rounded transition-all ${s.align === btn.id ? 'bg-white text-black' : 'text-white/40 hover:text-white'}`}>
                <SafeIcon icon={btn.icon} className="text-xs" />
              </button>
            ))}
          </div>
          <button onClick={() => updateTextStyle(styleKey, 'visible', s.visible !== false ? false : true)} className={`text-xs px-2 py-1 flex items-center gap-1 font-league ${s.visible !== false ? 'text-green-500' : 'text-red-500'}`}>
            <SafeIcon icon={s.visible !== false ? FiEye : FiEyeOff} /> {s.visible !== false ? 'VIS' : 'HID'}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <div className="md:col-span-6">
          <input type="text" value={s.text || ''} onChange={(e) => updateTextStyle(styleKey, 'text', e.target.value)} className="w-full bg-black/40 border border-white/10 p-2 text-white font-league text-lg" />
        </div>
        <div className="md:col-span-2">
          <div className="flex gap-1">
            <input type="color" value={s.color?.startsWith('#') ? s.color : '#ffffff'} onChange={(e) => updateTextStyle(styleKey, 'color', e.target.value)} className="h-9 w-9 bg-transparent border-0 cursor-pointer" />
            <input type="text" value={s.color} onChange={(e) => updateTextStyle(styleKey, 'color', e.target.value)} className="w-full bg-black/40 border border-white/10 text-[9px] text-white px-1 uppercase font-mono" />
          </div>
        </div>
        <div className="md:col-span-2">
          <input type="text" placeholder="Mob" value={s.sizeMobile} onChange={(e) => updateTextStyle(styleKey, 'sizeMobile', e.target.value)} className="w-full bg-black/40 border border-white/10 p-2 text-white text-[10px]" />
        </div>
        <div className="md:col-span-2">
          <input type="text" placeholder="Desk" value={s.sizeDesktop} onChange={(e) => updateTextStyle(styleKey, 'sizeDesktop', e.target.value)} className="w-full bg-black/40 border border-white/10 p-2 text-white text-[10px]" />
        </div>
      </div>
    </div>
  );
};

const Admin = () => {
  const {
    teams, globalScores, updateGlobalScores, showCountdown, showBadge, showRules, showGuests, eventDate, badgeText, pageTitle,
    badgeGap, gapBadgeTitle, gapHeroTitleCountdown, scoreboardGap, gapScoreTitleBoxes, gapBoxesPartials, gapScoresRoster,
    gapTitleTeams, gapTeamHeaderLine, gapTeamLineCaptain, gapTeamCaptainGrid, gapRosterEvents, gapEventsTitleGrid, gapCaptainRoster,
    updateConfig, schedule, addActivity, updateActivity, updateScore, updateActivityStatus, deleteActivity, gallery, gallerySections,
    addMediaBulk, deleteMedia, updateTeam, formatDateDDMMYYYY, updateGallerySectionTitle, addGallerySection, deleteGallerySection, updateGallerySections,
    rules, addRule, updateRule, deleteRule, guests, addGuest, updateGuest, deleteGuest, RenderName
  } = useContext(AppContext);

  const [activeTab, setActiveTab] = useState('texts');
  const [editingId, setEditingId] = useState(null);
  const [newAct, setNewAct] = useState({ day: 1, title: '', event_date: '', time: '', address: '', notes: '', counts_towards_score: true, status: 'upcoming' });
  const [bulkUrls, setBulkUrls] = useState('');
  const [selectedSectionId, setSelectedSectionId] = useState('');
  const [secTitle, setSecTitle] = useState('');
  const [secLevel, setSecLevel] = useState('main');
  const [secParentId, setSecParentId] = useState('');
  const [secVisible, setSecVisible] = useState(true);
  const [editingSecId, setEditingSecId] = useState(null);
  const [editingSecTitle, setEditingSecTitle] = useState('');
  const [draggedSecId, setDraggedSecId] = useState(null);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [newRule, setNewRule] = useState({ title: '', description: '' });
  const [editingGuestId, setEditingGuestId] = useState(null);
  const [newGuest, setNewGuest] = useState({ name: '', role: '', description: '', image: '' });

  const mediaTree = useMemo(() => {
    const allSections = gallerySections || [];
    const sectionMap = {};
    allSections.forEach(s => { sectionMap[s.id] = { ...s, items: [], subSections: [] }; });
    const uncategorizedMedia = [];
    (gallery || []).forEach(m => {
      if (m.section_visible === false) return;
      let placed = false;
      if (m.parentId) {
        if (sectionMap[m.parentId]) {
          sectionMap[m.parentId].items.push(m);
          placed = true;
        } else {
          sectionMap[m.parentId] = { id: m.parentId, title: m.section_title || 'Sezione Recuperata', order: m.section_order || 999, visible: true, items: [m], subSections: [] };
          placed = true;
        }
      } else if (m.section_title) {
        const legacySec = Object.values(sectionMap).find(s => s.title === m.section_title);
        if (legacySec) {
          legacySec.items.push(m);
          placed = true;
        } else {
          const newId = 'legacy_' + m.section_title.replace(/\s+/g, '_');
          sectionMap[newId] = { id: newId, title: m.section_title, order: m.section_order || 999, visible: true, items: [m], subSections: [] };
          placed = true;
        }
      }
      if (!placed) { uncategorizedMedia.push(m); }
    });
    const builtTree = [];
    Object.values(sectionMap).forEach(sec => {
      if (sec.parentId && sectionMap[sec.parentId]) {
        sectionMap[sec.parentId].subSections.push(sec);
      } else {
        builtTree.push(sec);
      }
    });
    builtTree.forEach(main => { main.subSections.sort((a, b) => (a.order || 0) - (b.order || 0)); });
    builtTree.sort((a, b) => (a.order || 0) - (b.order || 0));
    if (uncategorizedMedia.length > 0) {
      builtTree.push({ id: 'uncategorized', title: 'Altri Media', order: 99999, items: uncategorizedMedia, subSections: [] });
    }
    return builtTree;
  }, [gallery, gallerySections]);

  const handleSaveEvent = () => {
    if (!newAct.title.trim() || !newAct.event_date || !newAct.time.trim()) {
      alert("Attenzione: Titolo,Data e Ora sono campi obbligatori per salvare l'evento!");
      return;
    }
    if (editingId) {
      updateActivity(editingId, newAct);
      setEditingId(null);
    } else {
      addActivity(newAct);
    }
    setNewAct({ day: 1, title: '', event_date: '', time: '', address: '', notes: '', counts_towards_score: true, status: 'upcoming' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setNewAct({ day: 1, title: '', event_date: '', time: '', address: '', notes: '', counts_towards_score: true, status: 'upcoming' });
  };

  const handleSaveRule = () => {
    if (!newRule.title.trim() || !newRule.description.trim()) {
      alert("Titolo e Descrizione sono obbligatori.");
      return;
    }
    if (editingRuleId) {
      updateRule(editingRuleId, newRule);
      setEditingRuleId(null);
    } else {
      addRule(newRule);
    }
    setNewRule({ title: '', description: '' });
  };

  const handleCancelRuleEdit = () => {
    setEditingRuleId(null);
    setNewRule({ title: '', description: '' });
  };

  const handleSaveGuest = () => {
    if (!newGuest.name.trim()) {
      alert("Il Nome è obbligatorio.");
      return;
    }
    if (editingGuestId) {
      updateGuest(editingGuestId, newGuest);
      setEditingGuestId(null);
    } else {
      addGuest(newGuest);
    }
    setNewGuest({ name: '', role: '', description: '', image: '' });
  };

  const handleCancelGuestEdit = () => {
    setEditingGuestId(null);
    setNewGuest({ name: '', role: '', description: '', image: '' });
  };

  const handleAddSection = () => {
    if (!secTitle.trim()) return;
    if (secLevel === 'sub' && !secParentId) {
      alert("Devi selezionare la Sezione Principale a cui appartiene questa sottosezione.");
      return;
    }
    const parentId = secLevel === 'sub' ? secParentId : null;
    const siblings = (gallerySections || []).filter(s => s.parentId === parentId);
    const calculatedOrder = siblings.length > 0 ? Math.max(...siblings.map(s => s.order || 0)) + 1 : 1;
    const newSec = { id: Date.now().toString(), title: secTitle.trim(), order: calculatedOrder, visible: secVisible, parentId: parentId };
    addGallerySection(newSec);
    setSecTitle('');
    setSecVisible(true);
  };

  const handleDragStart = (e, id) => {
    setDraggedSecId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (!draggedSecId || draggedSecId === targetId) { setDraggedSecId(null); return; }
    const dragged = (gallerySections || []).find(s => s.id === draggedSecId);
    const target = (gallerySections || []).find(s => s.id === targetId);
    if (!dragged || !target || dragged.parentId !== target.parentId) { setDraggedSecId(null); return; }
    let siblings = (gallerySections || []).filter(s => s.parentId === dragged.parentId).sort((a, b) => (a.order || 0) - (b.order || 0));
    const draggedIdx = siblings.findIndex(s => s.id === draggedSecId);
    const targetIdx = siblings.findIndex(s => s.id === targetId);
    siblings.splice(draggedIdx, 1);
    siblings.splice(targetIdx, 0, dragged);
    siblings.forEach((s, i) => { s.order = i + 1; });
    const updatedSections = (gallerySections || []).map(s => {
      const sib = siblings.find(sib => sib.id === s.id);
      return sib ? sib : s;
    });
    updateGallerySections(updatedSections);
    setDraggedSecId(null);
  };

  const handleDragEnd = () => { setDraggedSecId(null); };

  const handleSaveSectionTitle = (sec) => {
    if (!editingSecTitle.trim() || editingSecTitle === sec.title) { setEditingSecId(null); return; }
    updateGallerySectionTitle(sec.id, sec.title, editingSecTitle.trim());
    setEditingSecId(null);
  };

  const handleRemoveSection = (id) => {
    if (window.confirm('Vuoi davvero eliminare questa sezione? Se è una Sezione Principale,anche le sue sottosezioni verranno eliminate. Le foto rimarranno in "Altri Media".')) {
      deleteGallerySection(id);
      if (selectedSectionId === id) setSelectedSectionId('');
    }
  };

  const handleToggleSectionVisibility = (id) => {
    const updated = (gallerySections || []).map(s => s.id === id ? { ...s, visible: !s.visible } : s);
    updateGallerySections(updated);
  };

  const NavBtn = ({ id, label, icon: Icon }) => (
    <button onClick={() => setActiveTab(id)} className={`px-5 py-4 font-league text-xl uppercase flex items-center gap-2 transition-all shrink-0 ${activeTab === id ? 'bg-white text-black' : 'text-white/40 hover:text-white hover:bg-white/5'}`}>
      <SafeIcon icon={Icon} /> {label}
    </button>
  );

  const mainSections = (gallerySections || []).filter(s => !s.parentId).sort((a, b) => (a.order || 0) - (b.order || 0));

  const renderSectionRow = (sec, isSub = false) => {
    const isDragged = draggedSecId === sec.id;
    return (
      <div key={sec.id} draggable onDragStart={(e) => handleDragStart(e, sec.id)} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, sec.id)} onDragEnd={handleDragEnd} className={`flex items-center justify-between p-3 transition-all duration-200 ${isSub ? 'bg-black/20 border-y border-r border-white/5 border-l-4 border-l-nba-blue ml-8 md:ml-12 mt-1' : 'bg-black/40 border-y border-r border-white/10 border-l-4 border-l-nba-red mt-4'} ${isDragged ? 'opacity-40 scale-[0.99] border-white' : ''}`}>
        <div className="flex items-center gap-4 flex-1">
          <div className="cursor-move text-white/30 hover:text-white p-2" title="Trascina per riordinare"><SafeIcon icon={FiMenu} className="text-xl" /></div>
          <div className="flex items-center gap-3 flex-1">
            {editingSecId === sec.id ? (
              <div className="flex items-center gap-2 w-full max-w-sm">
                <input type="text" value={editingSecTitle} onChange={(e) => setEditingSecTitle(e.target.value)} onBlur={() => handleSaveSectionTitle(sec)} onKeyDown={(e) => { if (e.key === 'Enter') handleSaveSectionTitle(sec); if (e.key === 'Escape') setEditingSecId(null); }} className="bg-black/80 border border-nba-blue text-white px-2 py-1 font-league text-2xl uppercase italic w-full outline-none tracking-wider shadow-[0_0_10px_rgba(29,66,138,0.3)]" autoFocus />
                <button onMouseDown={(e) => { e.preventDefault(); handleSaveSectionTitle(sec); }} className="p-1.5 bg-green-500/20 text-green-500 hover:bg-green-500 hover:text-black transition-colors rounded" title="Salva Modifica"><SafeIcon icon={FiCheck} /></button>
                <button onMouseDown={(e) => { e.preventDefault(); setEditingSecId(null); }} className="p-1.5 bg-red-500/20 text-red-500 hover:bg-red-500 hover:text-white transition-colors rounded" title="Annulla"><SafeIcon icon={FiX} /></button>
              </div>
            ) : (
              <span className={`font-league uppercase italic tracking-wider flex items-center gap-2 ${isSub ? 'text-2xl text-white/70' : 'text-3xl text-white'}`}>
                {isSub && <SafeIcon icon={FiCornerDownRight} className="text-white/20 text-xl shrink-0" />} {sec.title} <span className="text-[10px] font-sans text-white/20 ml-2 not-italic tracking-normal">(Ord: {sec.order})</span>
              </span>
            )}
          </div>
          {!sec.visible && <span className="text-[9px] bg-red-500/20 text-red-500 px-2 py-0.5 rounded uppercase tracking-widest font-black mr-4">Nascosta</span>}
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => { setEditingSecId(sec.id); setEditingSecTitle(sec.title); }} className="p-2 bg-white/5 text-white/40 hover:text-nba-blue transition-colors" title="Rinomina Sezione"><SafeIcon icon={FiEdit2} className="text-lg" /></button>
          <button onClick={() => handleToggleSectionVisibility(sec.id)} className="p-2 bg-white/5 text-white/40 hover:text-white transition-colors" title={sec.visible ? "Nascondi" : "Mostra"}><SafeIcon icon={sec.visible ? FiEye : FiEyeOff} className="text-lg" /></button>
          <button onClick={() => handleRemoveSection(sec.id)} className="p-2 bg-white/5 text-white/40 hover:text-red-500 transition-colors" title="Elimina Sezione"><SafeIcon icon={FiTrash2} className="text-lg" /></button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="sticky top-20 z-40 flex bg-nba-card border border-white/10 overflow-x-auto scrollbar-none shadow-2xl">
        <NavBtn id="texts" label="Contenuti" icon={FiType} />
        <NavBtn id="standings" label="Teams" icon={FiUsers} />
        <NavBtn id="matches" label="Programma" icon={FiCalendar} />
        <NavBtn id="scores" label="Punteggi" icon={FiAward} />
        <NavBtn id="media" label="Media" icon={FiImage} />
        <NavBtn id="rules" label="Regolamento" icon={FiBookOpen} />
        <NavBtn id="guests" label="Guests" icon={FiBriefcase} />
      </div>

      <div className="pt-2">
        {activeTab === 'texts' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pb-20">
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-nba-card border border-white/10 p-5 space-y-4">
                <h3 className="font-league text-2xl uppercase border-b border-white/10 pb-2"> Config Base </h3>
                <div>
                  <label className="text-[10px] font-black text-white/40 block mb-1 uppercase">Mostra Pagina Guests</label>
                  <button onClick={() => updateConfig({ show_guests: !showGuests })} className={`w-full p-3 text-sm font-black tracking-widest uppercase transition-colors border ${showGuests ? 'bg-green-500 border-green-500 text-black' : 'bg-red-500 border-red-500 text-white'}`}> {showGuests ? 'ON' : 'OFF'} </button>
                </div>
                <div>
                  <label className="text-[10px] font-black text-white/40 block mb-1 uppercase">Mostra Pagina Regolamento</label>
                  <button onClick={() => updateConfig({ show_rules: !showRules })} className={`w-full p-3 text-sm font-black tracking-widest uppercase transition-colors border ${showRules ? 'bg-green-500 border-green-500 text-black' : 'bg-red-500 border-red-500 text-white'}`}> {showRules ? 'ON' : 'OFF'} </button>
                </div>
                <div>
                  <label className="text-[10px] font-black text-white/40 block mb-1 uppercase">Countdown</label>
                  <button onClick={() => updateConfig({ show_countdown: !showCountdown })} className={`w-full p-3 text-sm font-black tracking-widest uppercase transition-colors border ${showCountdown ? 'bg-green-500 border-green-500 text-black' : 'bg-red-500 border-red-500 text-white'}`}> {showCountdown ? 'ON' : 'OFF'} </button>
                </div>
                <div>
                  <label className="text-[10px] font-black text-white/40 block mb-1 uppercase">Data Evento</label>
                  <input type="datetime-local" value={eventDate} onChange={(e) => updateConfig({ event_date: e.target.value })} className="w-full bg-black/40 border border-white/10 p-3 text-white font-mono text-xs" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-white/40 block mb-1 uppercase">Testo Badge Live</label>
                  <input type="text" value={badgeText} onChange={(e) => updateConfig({ badge_text: e.target.value })} className="w-full bg-black/40 border border-white/10 p-3 text-white font-league text-lg" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-white/40 block mb-1 uppercase">Mostra Badge Live</label>
                  <button onClick={() => updateConfig({ show_badge: !showBadge })} className={`w-full p-3 text-sm font-black tracking-widest uppercase transition-colors border ${showBadge ? 'bg-green-500 border-green-500 text-black' : 'bg-red-500 border-red-500 text-white'}`}> {showBadge ? 'ON' : 'OFF'} </button>
                </div>
                <div>
                  <label className="text-[10px] font-black text-white/40 block mb-1 uppercase">Titolo Scheda Browser</label>
                  <input type="text" value={pageTitle || ''} onChange={(e) => updateConfig({ page_title: e.target.value })} className="w-full bg-black/40 border border-white/10 p-3 text-white font-league text-lg" />
                </div>
              </div>
              <div className="bg-nba-card border border-white/10 p-5 space-y-4">
                <h3 className="font-league text-2xl uppercase border-b border-white/10 pb-2 flex items-center gap-2 text-white/60"> <SafeIcon icon={FiLayout} /> Layout Gaps (Home) </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="text-white/60 font-bold text-xs uppercase tracking-widest">1. Sezione Intro</h4>
                    <SpacingSlider label="Top Padding" value={badgeGap} onChange={v => updateConfig({ badge_gap: v })} />
                    <SpacingSlider label="Badge -> Titolo" value={gapBadgeTitle} onChange={v => updateConfig({ gap_badge_title: v })} />
                    <SpacingSlider label="Titolo -> Countdown" value={gapHeroTitleCountdown} onChange={v => updateConfig({ gap_hero_title_countdown: v })} />
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-white/60 font-bold text-xs uppercase tracking-widest">2. Sezione Score</h4>
                    <SpacingSlider label="Top Spazio Sezione" value={scoreboardGap} onChange={v => updateConfig({ scoreboard_gap: v })} />
                    <SpacingSlider label="Titolo -> Box Voti" value={gapScoreTitleBoxes} onChange={v => updateConfig({ gap_score_title_boxes: v })} />
                    <SpacingSlider label="Box Voti -> Parziali" value={gapBoxesPartials} onChange={v => updateConfig({ gap_boxes_partials: v })} />
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-white/60 font-bold text-xs uppercase tracking-widest">3. Sezione Atleti</h4>
                    <SpacingSlider label="Top Spazio Sezione" value={gapScoresRoster} onChange={v => updateConfig({ gap_scores_roster: v })} />
                    <SpacingSlider label="Titolo -> Griglie Squadre" value={gapTitleTeams} onChange={v => updateConfig({ gap_title_teams: v })} />
                    <SpacingSlider label="Nome Sq. -> Linea Sotto" value={gapTeamHeaderLine} onChange={v => updateConfig({ gap_team_header_line: v })} />
                    <SpacingSlider label="Linea -> Box Capitano" value={gapTeamLineCaptain} onChange={v => updateConfig({ gap_team_line_captain: v })} />
                    <SpacingSlider label="Box Capitano -> Griglia" value={gapTeamCaptainGrid} onChange={v => updateConfig({ gap_team_captain_grid: v })} />
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-white/60 font-bold text-xs uppercase tracking-widest">4. Sezione Programma</h4>
                    <SpacingSlider label="Top Spazio Sezione" value={gapRosterEvents} onChange={v => updateConfig({ gap_roster_events: v })} />
                    <SpacingSlider label="Titolo -> Griglia Match" value={gapEventsTitleGrid} onChange={v => updateConfig({ gap_events_title_grid: v })} />
                  </div>
                </div>
              </div>
              <div className="bg-nba-card border border-white/10 p-5 space-y-4">
                <h3 className="font-league text-2xl uppercase border-b border-white/10 pb-2 flex items-center gap-2 text-white/60"> <SafeIcon icon={FiLayout} /> Layout Gaps (Pagine) </h3>
                <div className="space-y-2">
                  <h4 className="text-white/60 font-bold text-xs uppercase tracking-widest">Pagina Teams</h4>
                  <SpacingSlider label="Capitano -> Roster" value={gapCaptainRoster} onChange={v => updateConfig({ gap_captain_roster: v })} />
                </div>
              </div>
            </div>
            <div className="lg:col-span-8 space-y-8">
              <div className="bg-nba-card border border-white/10 p-5 space-y-4">
                <h3 className="font-league text-2xl uppercase border-b border-white/10 pb-2 flex items-center gap-2"> <SafeIcon icon={FiSettings} className="text-nba-red" /> 1. Home Page </h3>
                <ContentBlock label="Titolo Grande Evento" styleKey="eventTitle" />
                <ContentBlock label="Label Sotto Scoreboard" styleKey="scoreboardTitle" />
                <ContentBlock label="Titolo Risultati Parziali" styleKey="partialsTitle" />
                <ContentBlock label="Titolo Sezione Atleti" styleKey="athletesTitle" />
                <ContentBlock label="Titolo Sezione Match" styleKey="homeEventsTitle" />
              </div>
              <div className="bg-nba-card border border-white/10 p-5 space-y-4">
                <h3 className="font-league text-2xl uppercase border-b border-white/10 pb-2 flex items-center gap-2"> <SafeIcon icon={FiLayout} className="text-nba-blue" /> 2. Titoli Pagine </h3>
                <ContentBlock label="Titolo Pagina Rosters" styleKey="rostersTitle" />
                <ContentBlock label="Titolo Pagina Programma" styleKey="scheduleTitle" />
                <ContentBlock label="Titolo Pagina Gallery" styleKey="galleryTitle" />
                <ContentBlock label="Titolo Pagina Regolamento" styleKey="rulesTitle" />
                <ContentBlock label="Titolo Pagina Guests" styleKey="guestsTitle" />
              </div>
              <div className="bg-nba-card border border-white/10 p-5 space-y-4">
                <h3 className="font-league text-2xl uppercase border-b border-white/10 pb-2 flex items-center gap-2"> <SafeIcon icon={FiGlobe} className="text-white/40" /> 3. Footer </h3>
                <ContentBlock label="Footer: Titolo Grande" styleKey="footerTitle" />
                <ContentBlock label="Footer: Sottotitolo 1" styleKey="footerSub1" />
                <ContentBlock label="Footer: Sottotitolo 2" styleKey="footerSub2" />
              </div>
              <div className="bg-nba-card border border-white/10 p-5 space-y-4">
                <h3 className="font-league text-2xl uppercase border-b border-white/10 pb-2 flex items-center gap-2"> <SafeIcon icon={FiGlobe} className="text-white/40" /> 4. Menu Navigazione </h3>
                <div className="grid grid-cols-1 gap-4">
                  <ContentBlock label="Nav: Home" styleKey="navHome" />
                  <ContentBlock label="Nav: Rosters" styleKey="navRosters" />
                  <ContentBlock label="Nav: Programma" styleKey="navSchedule" />
                  <ContentBlock label="Nav: Gallery" styleKey="navGallery" />
                  <ContentBlock label="Nav: Regolamento" styleKey="navRules" />
                  <ContentBlock label="Nav: Guests" styleKey="navGuests" />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'standings' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-20">
            {teams.map((team, idx) => (
              <div key={team.id} className="bg-nba-card border border-white/10 p-6 space-y-6">
                <div className="space-y-3 border-b border-white/10 pb-4">
                  <div className="flex items-center gap-4">
                    <input type="color" value={team.color} onChange={e => updateTeam(team.id, { color: e.target.value })} className="w-12 h-12 bg-transparent cursor-pointer shrink-0" />
                    <input type="text" value={team.name} onChange={e => updateTeam(team.id, { name: e.target.value })} className="flex-1 bg-black/40 border border-white/10 p-3 font-league text-3xl uppercase italic" placeholder="Nome Squadra" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-1">
                      <label className="text-[10px] font-black uppercase text-white/40 block mb-1">Divisione</label>
                      <input type="text" value={team.division || ''} onChange={e => updateTeam(team.id, { division: e.target.value })} className="w-full bg-black/40 border border-white/10 p-2 text-white text-sm uppercase" placeholder="Es: Division A" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-[10px] font-black uppercase text-white/40 block mb-1">Descrizione</label>
                      <input type="text" value={team.description || ''} onChange={e => updateTeam(team.id, { description: e.target.value })} className="w-full bg-black/40 border border-white/10 p-2 text-white text-sm" placeholder="Descrizione team..." />
                    </div>
                  </div>
                </div>
                <div className="bg-black/20 p-4 border border-white/5 space-y-4">
                  <div className="flex justify-between items-center text-[10px] font-black uppercase text-white/40 border-b border-white/5 pb-2">
                    <span>Roster Members</span>
                    <button onClick={() => { const members = [...(team.members || []), { name: 'Nuovo Atleta', role: 'Active Athlete', image: '', is_basket_captain: false, number: '' }]; updateTeam(team.id, { members }); }} className="text-white hover:text-green-500 flex items-center gap-1"> <SafeIcon icon={FiPlus} /> ADD </button>
                  </div>
                  <div className="space-y-4">
                    {team.members?.map((m, mIdx) => {
                      const isCap = team.captain_index === mIdx;
                      return (
                        <div key={mIdx} className={`relative p-3 rounded space-y-2 mt-4 transition-all duration-300 ${isCap ? 'bg-yellow-500/10 border border-yellow-500/50 shadow-[0_0_15px_rgba(234,179,8,0.1)]' : 'bg-black/40 border border-white/5'}`}>
                          {isCap && (
                            <div className="absolute -top-2.5 left-4 bg-yellow-500 text-black text-[9px] font-black px-2 py-0.5 uppercase tracking-widest rounded-sm shadow-[0_0_10px_rgba(234,179,8,0.4)] flex items-center gap-1">
                              <SafeIcon icon={FiStar} /> Designated Captain
                            </div>
                          )}
                          <div className="grid grid-cols-12 gap-1 items-center pt-1">
                            <input type="text" value={m.number || ''} onChange={e => { const members = team.members.map((mem, i) => i === mIdx ? { ...mem, number: e.target.value } : mem); updateTeam(team.id, { members }); }} className="col-span-1 bg-black/40 border border-white/10 p-2 text-sm text-center text-white font-league" placeholder="N°" />
                            <input type="text" value={m.name} onChange={e => { const members = team.members.map((mem, i) => i === mIdx ? { ...mem, name: e.target.value } : mem); updateTeam(team.id, { members }); }} className="col-span-3 bg-black/40 border border-white/10 p-2 text-sm text-white" placeholder="Nome" />
                            <input type="text" placeholder="Ruolo" value={m.role} onChange={e => { const members = team.members.map((mem, i) => i === mIdx ? { ...mem, role: e.target.value } : mem); updateTeam(team.id, { members }); }} className="col-span-3 bg-black/40 border border-white/10 p-2 text-[10px] italic text-white/60" />
                            <button onClick={() => updateTeam(team.id, { captain_index: mIdx })} className={`col-span-2 text-[9px] p-2 leading-none border tracking-widest ${isCap ? 'bg-yellow-500 text-black border-yellow-500 font-black' : 'border-white/10 text-white/20 hover:border-white/30'}`} title="Imposta come Designated Captain"> CAP </button>
                            <button onClick={() => { const members = team.members.map((mem, i) => ({ ...mem, is_basket_captain: i === mIdx ? !mem.is_basket_captain : false })); updateTeam(team.id, { members }); }} className={`col-span-2 text-[8px] p-2 leading-none border tracking-widest ${m.is_basket_captain ? 'bg-orange-500 text-black border-orange-500 font-black' : 'border-white/10 text-white/20 hover:border-white/30'}`} title="Capitano Squadra di Basket"> BASKET </button>
                            <button onClick={() => { const members = team.members.filter((_, i) => i !== mIdx); updateTeam(team.id, { members }); }} className="col-span-1 text-red-500 flex justify-center hover:scale-110 transition-transform"> <SafeIcon icon={FiTrash2} /> </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <SafeIcon icon={FiImage} className="text-white/40 shrink-0 text-sm" />
                            <input type="text" placeholder="URL Immagine Profilo" value={m.image || ''} onChange={e => { const members = team.members.map((mem, i) => i === mIdx ? { ...mem, image: e.target.value } : mem); updateTeam(team.id, { members }); }} className="w-full bg-black/40 border border-white/10 p-1.5 text-[10px] text-white/60 focus:text-white transition-colors" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'scores' && (
          <div className="space-y-8 pb-20">
            <div className="bg-nba-card border border-white/10 p-6 space-y-6">
              <h3 className="font-league text-3xl uppercase border-b border-white/10 pb-2 italic text-white flex items-center gap-2"> <SafeIcon icon={FiAward} /> Punteggio Generale Indipendente </h3>
              <p className="text-white/40 text-sm"> Questo punteggio è mostrato in Home Page ed è completamente slegato dai singoli eventi. Puoi aggiornarlo in qualsiasi momento. </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4">
                <div className="bg-black/40 p-6 border border-nba-red/30 rounded-lg text-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-nba-red/5"></div>
                  <div className="relative z-10">
                    <div className="font-league text-3xl text-white uppercase italic mb-4"> <RenderName name={teams[0]?.name || 'Team 1'} /> </div>
                    <div className="flex items-center justify-center gap-6">
                      <button onClick={() => updateGlobalScores(globalScores.t1 - 1, globalScores.t2)} className="w-12 h-12 rounded bg-white/5 border border-white/10 hover:bg-nba-red hover:text-white transition-colors text-2xl font-black">-</button>
                      <input type="number" value={globalScores.t1} onChange={(e) => updateGlobalScores(e.target.value, globalScores.t2)} className="w-24 bg-transparent text-center font-league text-6xl text-nba-red font-black outline-none border-b border-white/10 focus:border-nba-red" />
                      <button onClick={() => updateGlobalScores(globalScores.t1 + 1, globalScores.t2)} className="w-12 h-12 rounded bg-white/5 border border-white/10 hover:bg-nba-red hover:text-white transition-colors text-2xl font-black">+</button>
                    </div>
                  </div>
                </div>
                <div className="bg-black/40 p-6 border border-nba-blue/30 rounded-lg text-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-nba-blue/5"></div>
                  <div className="relative z-10">
                    <div className="font-league text-3xl text-white uppercase italic mb-4"> <RenderName name={teams[1]?.name || 'Team 2'} /> </div>
                    <div className="flex items-center justify-center gap-6">
                      <button onClick={() => updateGlobalScores(globalScores.t1, globalScores.t2 - 1)} className="w-12 h-12 rounded bg-white/5 border border-white/10 hover:bg-nba-blue hover:text-white transition-colors text-2xl font-black">-</button>
                      <input type="number" value={globalScores.t2} onChange={(e) => updateGlobalScores(globalScores.t1, e.target.value)} className="w-24 bg-transparent text-center font-league text-6xl text-nba-blue font-black outline-none border-b border-white/10 focus:border-nba-blue" />
                      <button onClick={() => updateGlobalScores(globalScores.t1, globalScores.t2 + 1)} className="w-12 h-12 rounded bg-white/5 border border-white/10 hover:bg-nba-blue hover:text-white transition-colors text-2xl font-black">+</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-nba-card border border-white/10 p-6 space-y-6">
              <h3 className="font-league text-3xl uppercase border-b border-white/10 pb-2 italic text-white flex items-center gap-2"> <SafeIcon icon={FiCalendar} /> Punteggi Singoli Eventi (Risultati Parziali) </h3>
              <p className="text-white/40 text-sm"> Aggiorna i risultati dei singoli match. Anche se imposti questi punteggi,non andranno a sommersi in automatico al punteggio generale. </p>
              <div className="space-y-6 mt-4">
                {Object.keys(schedule).sort((a, b) => Number(a) - Number(b)).map(day => (
                  <div key={day} className="space-y-3">
                    <h4 className="font-league text-2xl text-white/50 px-2 italic bg-white/5 p-2 rounded">Day {day}</h4>
                    {schedule[day].map(act => (
                      <div key={act.id} className="bg-black/40 border border-white/5 p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 rounded group hover:border-white/20 transition-colors">
                        <div className="flex-1">
                          <div className="font-league text-2xl uppercase italic text-white tracking-tight">{act.title}</div>
                          <div className="text-[10px] text-white/40 font-mono mt-1">{formatDateDDMMYYYY(act.event_date)} - {act.time}</div>
                        </div>
                        <div className="flex items-center gap-4 bg-black/60 p-3 rounded border border-white/5">
                          <div className="flex flex-col items-center">
                            <span className="text-[9px] font-bold text-nba-red uppercase mb-1 truncate max-w-[80px]" title={stripHtml(teams[0]?.name)}> {stripHtml(teams[0]?.name) || 'TEAM 1'} </span>
                            <div className="flex items-center gap-2">
                              <button onClick={() => updateScore(act.id, act.scoreT1 - 1, act.scoreT2)} className="w-6 h-6 bg-white/5 hover:bg-nba-red text-white flex items-center justify-center rounded text-xs">-</button>
                              <input type="number" value={act.scoreT1} onChange={(e) => updateScore(act.id, e.target.value, act.scoreT2)} className="w-12 bg-transparent text-center font-league text-2xl text-nba-red font-black outline-none" />
                              <button onClick={() => updateScore(act.id, act.scoreT1 + 1, act.scoreT2)} className="w-6 h-6 bg-white/5 hover:bg-nba-red text-white flex items-center justify-center rounded text-xs">+</button>
                            </div>
                          </div>
                          <span className="text-white/20 font-league text-xl">-</span>
                          <div className="flex flex-col items-center">
                            <span className="text-[9px] font-bold text-nba-blue uppercase mb-1 truncate max-w-[80px]" title={stripHtml(teams[1]?.name)}> {stripHtml(teams[1]?.name) || 'TEAM 2'} </span>
                            <div className="flex items-center gap-2">
                              <button onClick={() => updateScore(act.id, act.scoreT1, act.scoreT2 - 1)} className="w-6 h-6 bg-white/5 hover:bg-nba-blue text-white flex items-center justify-center rounded text-xs">-</button>
                              <input type="number" value={act.scoreT2} onChange={(e) => updateScore(act.id, act.scoreT1, e.target.value)} className="w-12 bg-transparent text-center font-league text-2xl text-nba-blue font-black outline-none" />
                              <button onClick={() => updateScore(act.id, act.scoreT1, act.scoreT2 + 1)} className="w-6 h-6 bg-white/5 hover:bg-nba-blue text-white flex items-center justify-center rounded text-xs">+</button>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 w-full md:w-auto">
                          <label className="text-[9px] text-white/40 uppercase">Stato Match</label>
                          <select value={act.status || 'upcoming'} onChange={(e) => updateActivityStatus(act.id, e.target.value)} className="bg-black border border-white/10 text-white text-xs p-2 outline-none cursor-pointer">
                            <option value="upcoming">IN ARRIVO</option>
                            <option value="live">LIVE</option>
                            <option value="final">FINALE</option>
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'matches' && (
          <div className="space-y-6 pb-20">
            <div className={`bg-nba-card border p-6 space-y-4 shadow-2xl transition-colors ${editingId ? 'border-nba-blue' : 'border-white/10'}`}>
              <h3 className={`font-league text-3xl uppercase border-b pb-2 ${editingId ? 'border-nba-blue text-nba-blue' : 'border-white/20'}`}> {editingId ? 'Modifica Evento' : 'Crea Nuovo Evento'} </h3>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                <div className="md:col-span-1">
                  <label className="text-[10px] font-bold text-white/40 block">DAY</label>
                  <input type="number" value={newAct.day} onChange={e => setNewAct({ ...newAct, day: e.target.value })} className="w-full bg-black/40 border border-white/10 p-2 text-white" />
                </div>
                <div className="md:col-span-3">
                  <label className="text-[10px] font-bold text-white/40 block">DATA *</label>
                  <input type="date" value={newAct.event_date} onChange={e => setNewAct({ ...newAct, event_date: e.target.value })} className="w-full bg-black/40 border border-white/10 p-2 text-white font-mono" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] font-bold text-white/40 block">ORA *</label>
                  <input type="time" value={newAct.time} onChange={e => setNewAct({ ...newAct, time: e.target.value })} className="w-full bg-black/40 border border-white/10 p-2 text-white font-mono" />
                </div>
                <div className="md:col-span-4">
                  <label className="text-[10px] font-bold text-white/40 block">TITOLO *</label>
                  <input type="text" value={newAct.title} onChange={e => setNewAct({ ...newAct, title: e.target.value })} className="w-full bg-black/40 border border-white/10 p-2 text-white font-league text-xl uppercase italic" />
                </div>
                <div className="md:col-span-2 flex items-center h-full pb-3">
                  <label className="flex items-center gap-2 text-[10px] font-bold text-white cursor-pointer select-none">
                    <input type="checkbox" checked={newAct.counts_towards_score} onChange={e => setNewAct({ ...newAct, counts_towards_score: e.target.checked })} className="w-4 h-4 accent-nba-red" /> MOSTRA SCORE
                  </label>
                </div>
                <div className="md:col-span-4">
                  <label className="text-[10px] font-bold text-white/40 block">INDIRIZZO</label>
                  <input type="text" value={newAct.address} onChange={e => setNewAct({ ...newAct, address: e.target.value })} className="w-full bg-black/40 border border-white/10 p-2 text-white" />
                </div>
                <div className="md:col-span-4">
                  <label className="text-[10px] font-bold text-white/40 block">NOTE</label>
                  <input type="text" value={newAct.notes} onChange={e => setNewAct({ ...newAct, notes: e.target.value })} className="w-full bg-black/40 border border-white/10 p-2 text-white" />
                </div>
                <div className="md:col-span-4 flex gap-2">
                  <button onClick={handleSaveEvent} className={`flex-1 text-black py-2 font-league text-2xl uppercase transition-colors ${editingId ? 'bg-nba-blue hover:bg-white' : 'bg-white hover:bg-nba-red hover:text-white'}`}> {editingId ? 'SALVA MODIFICHE' : 'CREA EVENTO'} </button>
                  {editingId && (
                    <button onClick={handleCancelEdit} className="bg-red-500/20 text-red-500 px-4 py-2 font-league text-2xl uppercase hover:bg-red-500 hover:text-white transition-colors"> ANNULLA </button>
                  )}
                </div>
              </div>
            </div>
            {Object.keys(schedule).sort((a, b) => Number(a) - Number(b)).map(day => (
              <div key={day} className="space-y-2">
                <h4 className="font-league text-4xl text-white/20 px-2 mt-8 italic border-l-4 border-white/10 ml-2">Day {day}</h4>
                {schedule[day].map(act => (
                  <div key={act.id} className={`bg-nba-card border p-4 flex flex-col lg:flex-row items-center gap-6 group transition-colors ${editingId === act.id ? 'border-nba-blue' : 'border-white/10 hover:border-nba-red'}`}>
                    <div className="w-24 text-white/40 font-mono text-sm leading-tight"> {formatDateDDMMYYYY(act.event_date)} <br /> {act.time} </div>
                    <div className="flex-1">
                      <div className="font-league text-3xl uppercase italic tracking-tight flex items-center gap-3">
                        {act.title}
                        {!act.counts_towards_score && (
                          <span className="text-[10px] font-sans font-bold bg-white/10 text-white/50 px-2 py-0.5 rounded uppercase not-italic">No Score</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <select value={act.status || 'upcoming'} onChange={(e) => updateActivityStatus(act.id, e.target.value)} className={`px-3 py-2 font-league text-lg skew-x-[-12deg] outline-none cursor-pointer transition-all ${act.status === 'live' ? 'bg-green-500 text-black font-black' : act.status === 'final' ? 'bg-white text-black font-black' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}>
                        <option value="upcoming" className="text-black bg-white">IN ARRIVO</option>
                        <option value="live" className="text-black bg-white">LIVE</option>
                        <option value="final" className="text-black bg-white">FINALE</option>
                      </select>
                      <button onClick={() => { setEditingId(act.id); setNewAct({ day: act.day_number, title: act.title, event_date: act.event_date || '', time: act.time || '', address: act.address || '', notes: act.notes || '', counts_towards_score: act.counts_towards_score !== false, status: act.status || 'upcoming' }); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="p-3 bg-white/5 text-white hover:bg-nba-blue transition-colors" title="Modifica Evento"> <SafeIcon icon={FiEdit2} className="text-xl" /> </button>
                      <button onClick={() => deleteActivity(act.id)} className="p-3 bg-white/5 text-white/40 hover:bg-red-500 hover:text-white transition-colors" title="Elimina Evento"> <SafeIcon icon={FiTrash2} className="text-xl" /> </button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'media' && (
          <div className="space-y-8 pb-20">
            <div className="bg-nba-card border border-white/10 p-6 space-y-4">
              <h3 className="font-league text-3xl uppercase border-b border-white/10 pb-2 italic">Gestione Sezioni Galleria</h3>
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-end">
                <div className="xl:col-span-4">
                  <label className="text-[10px] font-black text-white/40 block mb-1 uppercase">Titolo Nuova Sezione</label>
                  <input type="text" value={secTitle} onChange={e => setSecTitle(e.target.value)} className="w-full bg-black/40 border border-white/10 p-3 text-white outline-none focus:border-nba-blue font-league text-xl tracking-wider" placeholder="Es: Highlights o Eventi" />
                </div>
                <div className="xl:col-span-2">
                  <label className="text-[10px] font-black text-white/40 block mb-1 uppercase">Livello</label>
                  <select value={secLevel} onChange={e => { setSecLevel(e.target.value); setSecParentId(''); }} className="w-full bg-black/40 border border-white/10 p-3 text-white outline-none focus:border-nba-blue">
                    <option value="main">Principale</option>
                    <option value="sub">Sottosezione</option>
                  </select>
                </div>
                {secLevel === 'sub' && (
                  <div className="xl:col-span-3">
                    <label className="text-[10px] font-black text-white/40 block mb-1 uppercase">Appartiene a (Sezione Principale)</label>
                    <select value={secParentId} onChange={e => setSecParentId(e.target.value)} className="w-full bg-black/40 border border-white/10 p-3 text-white outline-none focus:border-nba-blue">
                      <option value="">Seleziona Sezione...</option>
                      {mainSections.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
                    </select>
                  </div>
                )}
                <div className={secLevel === 'sub' ? "xl:col-span-3 flex items-center gap-4 pb-3" : "xl:col-span-6 flex items-center gap-4 pb-3"}>
                  <label className="flex items-center gap-2 text-[10px] font-bold text-white cursor-pointer select-none shrink-0">
                    <input type="checkbox" checked={secVisible} onChange={e => setSecVisible(e.target.checked)} className="w-4 h-4 accent-nba-red" /> VISIBILE
                  </label>
                  <button onClick={handleAddSection} className="w-full bg-white text-black py-2.5 font-league text-xl uppercase italic hover:bg-nba-bluehover:text-white transition-all"> AGGIUNGI </button>
                </div>
              </div>
              {(gallerySections && gallerySections.length > 0) && (
                <div className="space-y-4 mt-8 relative">
                  <div className="text-[10px] text-white/40 uppercase mb-2">Struttura della tua galleria (Trascina l'icona ≡ per riordinare)</div>
                  <AnimatePresence>
                    {draggedSecId && (
                      <motion.div initial={{ height: 0, opacity: 0, marginBottom: 0 }} animate={{ height: 80, opacity: 1, marginBottom: 16 }} exit={{ height: 0, opacity: 0, marginBottom: 0 }} onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }} onDrop={(e) => { e.preventDefault(); handleRemoveSection(draggedSecId); setDraggedSecId(null); }} className="border-2 border-dashed border-red-500/50 bg-red-500/10 rounded-lg flex items-center justify-center text-red-500 flex-col gap-1 transition-colors hover:bg-red-500/20 overflow-hidden">
                        <SafeIcon icon={FiTrash2} className="text-2xl" />
                        <span className="font-league text-xl uppercase tracking-widest">Rilascia qui per eliminare</span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <div className="space-y-1">
                    {mainSections.map(main => (
                      <React.Fragment key={main.id}>
                        {renderSectionRow(main, false)}
                        {(gallerySections || []).filter(s => s.parentId === main.id).sort((a, b) => (a.order || 0) - (b.order || 0)).map(sub => (
                          renderSectionRow(sub, true)
                        ))}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="bg-nba-card border border-white/10 p-6 space-y-4">
              <h3 className="font-league text-3xl uppercase border-b border-white/10 pb-2 italic">Upload Media in Sezione</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-[10px] font-black text-white/40 block mb-1 uppercase">Sezione / Sottosezione</label>
                    <select value={selectedSectionId} onChange={e => setSelectedSectionId(e.target.value)} className="w-full bg-black/40 border border-white/10 p-3 text-white outline-none focus:border-nba-blue">
                      <option value="">Nessuna Sezione (Altri Media)</option>
                      {mainSections.map(main => (
                        <optgroup key={main.id} label={main.title}>
                          <option value={main.id}>{main.title} (Generale)</option>
                          {(gallerySections || []).filter(s => s.parentId === main.id).sort((a, b) => (a.order || 0) - (b.order || 0)).map(sub => (
                            <option key={sub.id} value={sub.id}>-- {sub.title}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-black text-white/40 block mb-1 uppercase">URL Immagini (Uno per riga)</label>
                  <textarea placeholder="https://image-url-1.jpg&#10;https://image-url-2.png" value={bulkUrls} onChange={e => setBulkUrls(e.target.value)} className="w-full bg-black/40 border border-white/10 p-4 h-40 text-white font-mono text-xs focus:border-nba-blue outline-none" />
                </div>
                <button onClick={() => { addMediaBulk(bulkUrls.split('\n').filter(u => u.trim()), 'image', selectedSectionId); setBulkUrls(''); }} className="w-full bg-white text-black py-4 font-league text-3xl uppercase italic hover:bg-nba-blue hover:text-white transition-all"> CARICA IN GALLERY </button>
              </div>
            </div>
            <div className="bg-nba-card border border-white/10 p-6 space-y-6">
              <h3 className="font-league text-3xl uppercase border-b border-white/10 pb-2 italic">Libreria Media</h3>
              {mediaTree.length > 0 ? (
                <div className="space-y-8">
                  {mediaTree.map(sec => (
                    <div key={sec.id} className="space-y-4">
                      <div className="flex items-center gap-3 bg-black/40 p-3 border border-white/5 border-l-4 border-l-nba-red">
                        <SafeIcon icon={FiFolder} className="text-nba-red text-2xl" />
                        <h4 className="font-league text-2xl uppercase tracking-wider m-0 leading-none pt-1">{sec.title}</h4>
                        <span className="text-white/40 text-xs font-black uppercase tracking-widest ml-auto"> {sec.items.length} media </span>
                      </div>
                      {sec.items.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                          {sec.items.map(m => (
                            <div key={m.id} className="aspect-square bg-black/40 border border-white/10 relative group overflow-hidden shadow-lg">
                              <img src={m.url} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500" alt="Media view" />
                              <button onClick={() => deleteMedia(m.id)} className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-xl hover:bg-red-600 hover:scale-110"> <SafeIcon icon={FiTrash2} /> </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {sec.subSections && sec.subSections.length > 0 && (
                        <div className="space-y-6 mt-4 pl-6 md:pl-12 border-l-2 border-white/10">
                          {sec.subSections.map(sub => (
                            <div key={sub.id} className="space-y-4">
                              <div className="flex items-center gap-3 bg-black/20 p-2 border border-white/5 border-l-4 border-l-nba-blue">
                                <SafeIcon icon={FiFolder} className="text-nba-blue text-xl" />
                                <h5 className="font-league text-xl uppercase tracking-wider text-white/80 m-0 leading-none pt-1">{sub.title}</h5>
                                <span className="text-white/40 text-[10px] font-black uppercase tracking-widest ml-auto"> {sub.items.length} media </span>
                              </div>
                              {sub.items.length > 0 ? (
                                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                  {sub.items.map(m => (
                                    <div key={m.id} className="aspect-square bg-black/40 border border-white/10 relative group overflow-hidden shadow-lg">
                                      <img src={m.url} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500" alt="Media view" />
                                      <button onClick={() => deleteMedia(m.id)} className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-xl hover:bg-red-600 hover:scale-110"> <SafeIcon icon={FiTrash2} /> </button>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-white/20 text-xs italic font-league uppercase tracking-widest py-2"> Nessun media in questa sottosezione </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {sec.items.length === 0 && (!sec.subSections || sec.subSections.length === 0) && (
                        <div className="text-white/20 text-sm italic font-league uppercase tracking-widest py-2 pl-4"> Nessun elemento in questa sezione </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-white/30 font-league text-2xl uppercase italic text-center py-8"> Nessun media caricato. </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'rules' && (
          <div className="space-y-6 pb-20">
            <div className={`bg-nba-card border p-6 space-y-4 shadow-2xl transition-colors ${editingRuleId ? 'border-nba-blue' : 'border-white/10'}`}>
              <h3 className={`font-league text-3xl uppercase border-b pb-2 ${editingRuleId ? 'border-nba-blue text-nba-blue' : 'border-white/20'}`}> {editingRuleId ? 'Modifica Regolamento' : 'Aggiungi Nuovo Regolamento'} </h3>
              <div className="grid grid-cols-1 gap-4 items-end">
                <div>
                  <label className="text-[10px] font-bold text-white/40 block mb-1">TITOLO REGOLAMENTO / GIOCO *</label>
                  <input type="text" value={newRule.title} onChange={e => setNewRule({ ...newRule, title: e.target.value })} className="w-full bg-black/40 border border-white/10 p-3 text-white font-league text-2xl uppercase italic" placeholder="Es: Basket 3v3" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-white/40 block mb-1">DESCRIZIONE *</label>
                  <textarea value={newRule.description} onChange={e => setNewRule({ ...newRule, description: e.target.value })} className="w-full bg-black/40 border border-white/10 p-3 text-white min-h-[150px] font-body" placeholder="Inserisci il testo del regolamento qui..." />
                </div>
                <div className="flex gap-2 pt-2">
                  <button onClick={handleSaveRule} className={`flex-1 text-black py-3 font-league text-2xl uppercase transition-colors ${editingRuleId ? 'bg-nba-blue hover:bg-white' : 'bg-white hover:bg-nba-red hover:text-white'}`}> {editingRuleId ? 'SALVA MODIFICHE' : 'CREA REGOLAMENTO'} </button>
                  {editingRuleId && (
                    <button onClick={handleCancelRuleEdit} className="bg-red-500/20 text-red-500 px-6 py-3 font-league text-2xl uppercase hover:bg-red-500 hover:text-white transition-colors"> ANNULLA </button>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-4 mt-8">
              <h4 className="font-league text-4xl text-white/20 px-2 italic border-l-4 border-white/10 ml-2">Regolamenti Esistenti</h4>
              {rules && rules.length > 0 ? (
                rules.map(r => (
                  <div key={r.id} className={`bg-nba-card border p-6 flex flex-col md:flex-row items-start md:items-center gap-6 group transition-colors ${editingRuleId === r.id ? 'border-nba-blue' : 'border-white/10 hover:border-nba-red'}`}>
                    <div className="flex-1">
                      <h4 className="font-league text-3xl uppercase italic tracking-tight text-white mb-2">{r.title}</h4>
                      <p className="text-white/60 text-sm whitespace-pre-wrap">{r.description}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => { setEditingRuleId(r.id); setNewRule({ title: r.title, description: r.description }); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="p-3 bg-white/5 text-white hover:bg-nba-blue transition-colors" title="Modifica Regolamento"> <SafeIcon icon={FiEdit2} className="text-xl" /> </button>
                      <button onClick={() => { if (window.confirm('Sei sicuro di voler eliminare questo regolamento?')) deleteRule(r.id); }} className="p-3 bg-white/5 text-white/40 hover:bg-red-500 hover:text-white transition-colors" title="Elimina Regolamento"> <SafeIcon icon={FiTrash2} className="text-xl" /> </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-white/30 font-league text-2xl uppercase italic py-8 border border-white/5 bg-black/20 text-center"> Nessun regolamento inserito. </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'guests' && (
          <div className="space-y-6 pb-20">
            <div className={`bg-nba-card border p-6 space-y-4 shadow-2xl transition-colors ${editingGuestId ? 'border-nba-blue' : 'border-white/10'}`}>
              <h3 className={`font-league text-3xl uppercase border-b pb-2 ${editingGuestId ? 'border-nba-blue text-nba-blue' : 'border-white/20'}`}> {editingGuestId ? 'Modifica Ospite' : 'Aggiungi Nuovo Ospite'} </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <div>
                  <label className="text-[10px] font-bold text-white/40 block mb-1">NOME *</label>
                  <input type="text" value={newGuest.name} onChange={e => setNewGuest({ ...newGuest, name: e.target.value })} className="w-full bg-black/40 border border-white/10 p-3 text-white font-league text-2xl uppercase italic" placeholder="Es: Mario Rossi" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-white/40 block mb-1">RUOLO / TITOLO</label>
                  <input type="text" value={newGuest.role} onChange={e => setNewGuest({ ...newGuest, role: e.target.value })} className="w-full bg-black/40 border border-white/10 p-3 text-white font-league text-xl uppercase" placeholder="Es: Event Director" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] font-bold text-white/40 block mb-1">URL FOTO PROFILO</label>
                  <input type="text" value={newGuest.image} onChange={e => setNewGuest({ ...newGuest, image: e.target.value })} className="w-full bg-black/40 border border-white/10 p-3 text-white font-mono text-sm" placeholder="https://..." />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] font-bold text-white/40 block mb-1">DESCRIZIONE (Opzionale)</label>
                  <textarea value={newGuest.description} onChange={e => setNewGuest({ ...newGuest, description: e.target.value })} className="w-full bg-black/40 border border-white/10 p-3 text-white min-h-[100px] font-body" placeholder="Breve biografia o descrizione..." />
                </div>
                <div className="md:col-span-2 flex gap-2 pt-2">
                  <button onClick={handleSaveGuest} className={`flex-1 text-black py-3 font-league text-2xl uppercase transition-colors ${editingGuestId ? 'bg-nba-blue hover:bg-white' : 'bg-white hover:bg-nba-red hover:text-white'}`}> {editingGuestId ? 'SALVA MODIFICHE' : 'CREA OSPITE'} </button>
                  {editingGuestId && (
                    <button onClick={handleCancelGuestEdit} className="bg-red-500/20 text-red-500 px-6 py-3 font-league text-2xl uppercase hover:bg-red-500 hover:text-white transition-colors"> ANNULLA </button>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-4 mt-8">
              <h4 className="font-league text-4xl text-white/20 px-2 italic border-l-4 border-white/10 ml-2">Ospiti Inseriti</h4>
              {guests && guests.length > 0 ? (
                guests.map(g => (
                  <div key={g.id} className={`bg-nba-card border p-4 flex flex-col sm:flex-row items-center gap-6 group transition-colors ${editingGuestId === g.id ? 'border-nba-blue' : 'border-white/10 hover:border-nba-red'}`}>
                    <div className="w-20 h-20 bg-black/50 border border-white/10 shrink-0 overflow-hidden">
                      {g.image ? <img src={g.image} alt={g.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/20"><SafeIcon icon={FiUsers} className="text-3xl" /></div>}
                    </div>
                    <div className="flex-1 text-center sm:text-left">
                      <h4 className="font-league text-3xl uppercase italic tracking-tight text-white m-0 leading-none">{g.name}</h4>
                      <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mt-1">{g.role || 'Special Guest'}</div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => { setEditingGuestId(g.id); setNewGuest({ name: g.name, role: g.role, description: g.description, image: g.image }); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="p-3 bg-white/5 text-white hover:bg-nba-blue transition-colors" title="Modifica Ospite"> <SafeIcon icon={FiEdit2} className="text-xl" /> </button>
                      <button onClick={() => { if (window.confirm('Sei sicuro di voler eliminare questo ospite?')) deleteGuest(g.id); }} className="p-3 bg-white/5 text-white/40 hover:bg-red-500 hover:text-white transition-colors" title="Elimina Ospite"> <SafeIcon icon={FiTrash2} className="text-xl" /> </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-white/30 font-league text-2xl uppercase italic py-8 border border-white/5 bg-black/20 text-center"> Nessun ospite inserito. </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;